// Admin-Frontend der Status-Plattform (Konzept Abschnitt 9).
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { UserManager } from 'oidc-client-ts';

import { createApi, fmt, productName } from './api.ts';
import type { Maintenance, Me, Scope, State } from './api.ts';
import { breakglassLogin, clearBreakglass, oidcReachable, storedBreakglass } from './auth.ts';
import type { Config, Session } from './auth.ts';
import { Incidents } from './Incidents.tsx';
import { MaintenanceForm } from './MaintenanceForm.tsx';
import { Passkeys, Tokens } from './Settings.tsx';

type Tab = 'overview' | 'new' | 'incidents' | 'passkeys' | 'tokens';

function scopeText(s: Scope): string {
  if (s.kind === 'global') return 'alle Produkte';
  if (s.kind === 'products') return s.products.map(productName).join(', ');
  return `Tenant ${s.tenant}${s.products?.length ? ` (${s.products.map(productName).join(', ')})` : ''}`;
}

function Login({ config, users }: { config: Config; users: UserManager }) {
  const [reachable, setReachable] = useState<boolean | null>(null);
  const [error, setError] = useState('');
  useEffect(() => {
    void oidcReachable(config).then(setReachable);
  }, [config]);
  return (
    <main className="login">
      <h1>Status-Admin</h1>
      <p>Wartungen, Störungen und Statuspage von TK-Schulsoftware.</p>
      <button className="primary" disabled={reachable === false} onClick={() => void users.signinRedirect()}>
        Anmelden mit TK-Login intern
      </button>
      {reachable === false && (
        <div className="emergency">
          <p>
            <strong>authentik antwortet nicht.</strong> Mit einem vorher registrierten Passkey kannst du dich direkt am Writer
            anmelden. Jede Aktion wird als Notfall-Anmeldung protokolliert.
          </p>
          <button
            className="danger"
            onClick={() =>
              void breakglassLogin().then(
                () => location.reload(),
                (e: unknown) => setError(e instanceof Error ? e.message : String(e)),
              )
            }
          >
            Notfall-Anmeldung
          </button>
        </div>
      )}
      {error && <p className="error">{error}</p>}
    </main>
  );
}

export function App({ config, users }: { config: Config; users: UserManager }) {
  const [session, setSession] = useState<Session | null>(storedBreakglass());
  const [checked, setChecked] = useState(false);
  const [me, setMe] = useState<Me | null>(null);
  const [state, setState] = useState<State | null>(null);
  const [tab, setTab] = useState<Tab>('overview');
  const [editing, setEditing] = useState<Maintenance | undefined>();
  const [toast, setToast] = useState<{ msg: string; error: boolean } | null>(null);

  useEffect(() => {
    if (session) return setChecked(true);
    void users.getUser().then((u) => {
      if (u && !u.expired) setSession({ token: u.access_token, kind: 'oidc', expiresAt: (u.expires_at ?? 0) * 1000 });
      setChecked(true);
    });
  }, [users, session]);

  const signOut = useCallback(() => {
    clearBreakglass();
    setSession(null);
    void users.removeUser();
  }, [users]);

  const api = useMemo(() => createApi(() => session, signOut), [session, signOut]);
  const notify = (msg: string, error = false) => setToast({ msg, error });
  const reload = useCallback(() => {
    void api.state().then(setState, (e: unknown) => setToast({ msg: e instanceof Error ? e.message : String(e), error: true }));
  }, [api]);

  useEffect(() => {
    if (!session) return;
    void api.me().then(setMe, () => undefined);
    reload();
    const t = setInterval(reload, 30_000);
    return () => clearInterval(t);
  }, [session, api, reload]);

  if (!checked) return null;
  if (!session) return <Login config={config} users={users} />;
  if (!me || !state) return <main className="login">Lade …</main>;

  const roles = me.roles;
  const now = new Date().toISOString();
  const maintenances = Object.values(state.maintenances).sort((a, b) => a.starts_at.localeCompare(b.starts_at));
  const active = maintenances.filter((m) => m.starts_at <= now && now < m.ends_at);
  const planned = maintenances.filter((m) => m.starts_at > now);

  const act = async (what: string, fn: () => Promise<unknown>) => {
    try {
      await fn();
      notify(what);
      reload();
    } catch (e) {
      notify(e instanceof Error ? e.message : String(e), true);
    }
  };

  const row = (m: Maintenance) => {
    const needsApproval = m.scope.kind === 'global' && !m.approval;
    const mayApprove = needsApproval && roles.includes('approver') && m.updated_by !== me.id;
    return (
      <tr key={m.id} className={needsApproval ? 'pending' : ''}>
        <td>
          <b>{m.message.de}</b>
          <div className="meta">
            {m.id} · {m.type} · <span className={`mode mode-${m.mode}`}>{m.mode}</span> · {scopeText(m.scope)}
            {m.public && ' · Statuspage'}
            {m.internal_ref && ` · intern: ${m.internal_ref}`}
          </div>
          {needsApproval && <div className="warn">Wartet auf Freigabe (Vier Augen) – noch nicht wirksam.</div>}
        </td>
        <td>
          {fmt(m.starts_at)} – {fmt(m.ends_at)}
        </td>
        <td className="actions">
          {mayApprove && (
            <button className="primary" onClick={() => void act('Freigegeben.', () => api.approveMaintenance(m.id))}>
              Freigeben
            </button>
          )}
          {m.starts_at <= now && now < m.ends_at && (
            <button className="danger" onClick={() => void act(`${m.id} beendet.`, () => api.endMaintenance(m.id))}>
              Jetzt beenden
            </button>
          )}
          <button
            onClick={() => {
              setEditing(m);
              setTab('new');
            }}
          >
            Bearbeiten
          </button>
          {m.starts_at > now && (
            <button onClick={() => void act(`${m.id} gelöscht.`, () => api.deleteMaintenance(m.id))}>Löschen</button>
          )}
        </td>
      </tr>
    );
  };

  return (
    <>
      {me.auth === 'breakglass' && (
        <div className="breakglass-bar">
          Notfall-Anmeldung aktiv (bis{' '}
          {new Date(session.expiresAt).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}) – jede Aktion wird als
          auth=breakglass protokolliert.
        </div>
      )}
      <header className="top">
        <strong>Status-Admin</strong>
        <nav>
          {(
            [
              ['overview', 'Übersicht'],
              ['new', 'Neuer Eintrag'],
              ['incidents', 'Störungen & Statuspage'],
              ['passkeys', 'Passkeys'],
              ...(roles.includes('global') ? ([['tokens', 'Writer-Tokens']] as const) : []),
            ] as const
          ).map(([k, label]) => (
            <button
              key={k}
              className={tab === k ? 'active' : ''}
              onClick={() => {
                setEditing(undefined);
                setTab(k);
              }}
            >
              {label}
            </button>
          ))}
        </nav>
        <span className="who">
          {me.name} · {roles.filter((r) => r !== 'staff').join(', ') || 'nur lesen'} · <button onClick={signOut}>Abmelden</button>
        </span>
      </header>
      {toast && (
        <div className={`toast ${toast.error ? 'error' : ''}`} onClick={() => setToast(null)}>
          {toast.msg}
        </div>
      )}
      <main>
        {tab === 'overview' && (
          <>
            <section className="card">
              <h2>Aktiv</h2>
              {active.length ? (
                <table>
                  <tbody>{active.map(row)}</tbody>
                </table>
              ) : (
                <p className="empty">Keine aktive Wartung.</p>
              )}
            </section>
            <section className="card">
              <h2>Geplant</h2>
              {planned.length ? (
                <table>
                  <tbody>{planned.map(row)}</tbody>
                </table>
              ) : (
                <p className="empty">Nichts geplant.</p>
              )}
            </section>
            <p className="meta">
              Stand: Version {state.version}, {fmt(state.updated_at)}. Änderungen sind nach höchstens 30 Sekunden Cache plus
              Abrufintervall in den Apps sichtbar.
            </p>
          </>
        )}
        {tab === 'new' && (
          <MaintenanceForm
            key={editing?.id ?? 'new'}
            api={api}
            version={state.version}
            roles={roles}
            {...(editing ? { existing: editing } : {})}
            onDone={(msg) => {
              notify(msg);
              setEditing(undefined);
              setTab('overview');
              reload();
            }}
            onCancel={() => {
              setEditing(undefined);
              setTab('overview');
            }}
          />
        )}
        {tab === 'incidents' && <Incidents api={api} state={state} roles={roles} notify={notify} reload={reload} />}
        {tab === 'passkeys' && <Passkeys api={api} me={me} notify={notify} />}
        {tab === 'tokens' && <Tokens api={api} notify={notify} />}
      </main>
    </>
  );
}
